import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import StormOutlinedIcon from '@mui/icons-material/StormOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import UmbrellaOutlinedIcon from '@mui/icons-material/UmbrellaOutlined';
import WbTwilightOutlinedIcon from '@mui/icons-material/WbTwilightOutlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';

import {
  IPrevisaoDetalhada,
  IPrevisaoTempo,
  ISeveridadeAlerta,
  PrevisaoTempoService,
} from '../../shared/services/api/mercado';
import { CABECALHO_CLARO, CABECALHO_ESCURO, ICoresCabecalho } from './coresCabecalho';
import { IconeCondicao } from './IconeCondicao';

const SEVERIDADE_ALERTA: Record<ISeveridadeAlerta, 'error' | 'warning' | 'success'> = {
  perigo: 'error',
  atencao: 'warning',
  positivo: 'success',
};

const DIRECOES = ['N', 'NE', 'L', 'SE', 'S', 'SO', 'O', 'NO'];
const direcaoDoVento = (graus: number) => DIRECOES[Math.round(((graus % 360) + 360) % 360 / 45) % 8];

const nivelUv = (uv: number) => {
  if (uv < 3) return 'Baixo';
  if (uv < 6) return 'Moderado';
  if (uv < 8) return 'Alto';
  if (uv < 11) return 'Muito alto';
  return 'Extremo';
};

const horaCurta = (iso: string) => iso.slice(11, 16);

const nomeDoDia = (dataIso: string, indice: number) => {
  if (indice === 0) return 'Hoje';
  if (indice === 1) return 'Amanhã';
  return new Date(`${dataIso}T00:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' }).split('-')[0];
};

const dataCurta = (dataIso: string) =>
  new Date(`${dataIso}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

interface IDialogPrevisaoTempoProps {
  open: boolean;
  onClose: () => void;
  previsao: IPrevisaoTempo;
  // Cores do cabeçalho do painel inicial — o topo do dialog repete o mesmo
  // fundo; o corpo segue o tema (claro/escuro).
  cores: ICoresCabecalho;
}

// Previsão do tempo detalhada, aberta ao clicar no painel do cabeçalho:
// condições de agora, alertas, as próximas 24 horas e os próximos 7 dias.
// Os dados detalhados são buscados só quando o dialog abre.
export const DialogPrevisaoTempo: React.FC<IDialogPrevisaoTempoProps> = ({ open, onClose, previsao, cores }) => {
  const theme = useTheme();
  const telaPequena = useMediaQuery(theme.breakpoints.down('sm'));
  const coresCondicao = theme.palette.mode === 'dark' ? CABECALHO_ESCURO.condicao : CABECALHO_CLARO.condicao;

  const [detalhe, setDetalhe] = useState<IPrevisaoDetalhada | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = () => {
    setCarregando(true);
    setErro(null);
    PrevisaoTempoService.obterPrevisaoDetalhada(previsao.latitude, previsao.longitude)
      .then(setDetalhe)
      .catch((e: Error) => setErro(e.message || 'Não foi possível carregar a previsão detalhada.'))
      .finally(() => setCarregando(false));
  };

  // Recarrega a cada abertura (e se a cidade mudar) — previsão fica velha rápido.
  useEffect(() => {
    if (open) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, previsao.latitude, previsao.longitude]);

  const atual = detalhe?.atual;
  const hoje = detalhe?.dias[0];

  // Escala das barras de temperatura dos 7 dias (mín da semana → máx da semana).
  const minSemana = detalhe ? Math.min(...detalhe.dias.map(d => d.tempMin)) : 0;
  const maxSemana = detalhe ? Math.max(...detalhe.dias.map(d => d.tempMax)) : 0;
  const amplitude = Math.max(1, maxSemana - minSemana);

  const blocos = atual && hoje ? [
    { icone: <ThermostatOutlinedIcon />, rotulo: 'Sensação térmica', valor: `${Math.round(atual.sensacao)}°C` },
    { icone: <WaterDropOutlinedIcon />, rotulo: 'Umidade', valor: `${Math.round(atual.umidade)}%` },
    { icone: <AirOutlinedIcon />, rotulo: 'Vento', valor: `${Math.round(atual.velocidadeVento)} km/h ${direcaoDoVento(atual.direcaoVento)}` },
    { icone: <StormOutlinedIcon />, rotulo: 'Rajadas', valor: `${Math.round(atual.rajada)} km/h` },
    { icone: <UmbrellaOutlinedIcon />, rotulo: 'Chuva hoje', valor: `${Math.round(hoje.probabilidadeChuva)}% · ${hoje.chuvaMm.toFixed(1).replace('.', ',')} mm` },
    { icone: <WbSunnyOutlinedIcon />, rotulo: 'Índice UV (máx.)', valor: `${Math.round(hoje.uvMax)} · ${nivelUv(hoje.uvMax)}` },
    { icone: <SpeedOutlinedIcon />, rotulo: 'Pressão', valor: `${Math.round(atual.pressao)} hPa` },
    { icone: <CloudOutlinedIcon />, rotulo: 'Nuvens', valor: `${Math.round(atual.nuvens)}%` },
    { icone: <WbTwilightOutlinedIcon />, rotulo: 'Nascer do sol', valor: horaCurta(hoje.nascerSol) },
    { icone: <NightsStayOutlinedIcon />, rotulo: 'Pôr do sol', valor: horaCurta(hoje.porSol) },
  ] : [];

  const tituloSecao = (texto: string) => (
    <Typography variant="overline" fontWeight={700} color="text.secondary" display="block" mb={1} lineHeight={1.5}>
      {texto}
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={telaPequena} scroll="paper">
      {/* Topo: agora */}
      <Box
        sx={{
          background: cores.fundo,
          color: cores.texto,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 2, sm: 2.5 },
          position: 'relative',
          borderBottom: `1px solid ${cores.painelBorda}`,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Fechar"
          sx={{ position: 'absolute', top: 8, right: 8, color: cores.textoSecundario, '&:hover': { color: cores.texto } }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Box display="flex" alignItems="center" gap={0.5} pr={5}>
          <RoomOutlinedIcon sx={{ fontSize: 18, color: cores.destaque }} />
          <Typography fontWeight={700} noWrap>
            {previsao.cidade}{previsao.origemLocalizacao === 'ip' ? ' (aprox.)' : ''}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2} mt={1.5} flexWrap="wrap">
          <IconeCondicao icone={(atual ?? previsao.atual).condicao.icone} tamanho={telaPequena ? 52 : 64} cores={cores.condicao} />
          <Box>
            <Typography sx={{ fontSize: { xs: 40, sm: 52 }, fontWeight: 700, lineHeight: 1 }}>
              {Math.round((atual ?? previsao.atual).temperatura)}°C
            </Typography>
            <Typography variant="body2" sx={{ color: cores.textoSecundario, mt: 0.5 }}>
              {(atual ?? previsao.atual).condicao.texto}
              {atual ? ` · sensação de ${Math.round(atual.sensacao)}°C` : ''}
            </Typography>
          </Box>
          {hoje && (
            <Box ml={{ xs: 0, sm: 'auto' }} textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="body2" fontWeight={700}>
                Máx. {Math.round(hoje.tempMax)}° · Mín. {Math.round(hoje.tempMin)}°
              </Typography>
              <Typography variant="caption" sx={{ color: cores.chuva }}>
                {Math.round(hoje.probabilidadeChuva)}% de chance de chuva hoje
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        {erro && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={carregar}>Tentar novamente</Button>} sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {previsao.alertas.length > 0 && (
          <Box display="flex" flexDirection="column" gap={1} mb={3}>
            {previsao.alertas.map(alerta => (
              <Alert key={alerta.titulo} severity={SEVERIDADE_ALERTA[alerta.severidade]} variant="outlined" sx={{ py: 0.25 }}>
                <AlertTitle sx={{ mb: 0.25, fontSize: 14 }}>{alerta.titulo}</AlertTitle>
                {alerta.mensagem}
              </Alert>
            ))}
          </Box>
        )}

        {/* Detalhes de agora */}
        {tituloSecao('Detalhes de agora')}
        <Box
          display="grid"
          gap={1}
          mb={3}
          sx={{ gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' } }}
        >
          {carregando && !detalhe
            ? Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} variant="rounded" height={64} />)
            : blocos.map(bloco => (
              <Box
                key={bloco.rotulo}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.action.hover,
                  minWidth: 0,
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5} color="text.secondary" sx={{ '& svg': { fontSize: 16 } }}>
                  {bloco.icone}
                  <Typography variant="caption" noWrap>{bloco.rotulo}</Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} mt={0.5} noWrap title={bloco.valor}>{bloco.valor}</Typography>
              </Box>
            ))}
        </Box>

        {/* Próximas 24 horas */}
        {tituloSecao('Próximas 24 horas')}
        <Box
          display="flex"
          gap={1}
          mb={3}
          pb={1}
          sx={{ overflowX: 'auto', scrollSnapType: 'x proximity' }}
        >
          {carregando && !detalhe
            ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} variant="rounded" width={64} height={104} sx={{ flexShrink: 0 }} />)
            : detalhe?.horas.map((hora, i) => (
              <Box
                key={hora.hora}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={0.5}
                py={1}
                sx={{
                  flex: '0 0 64px',
                  scrollSnapAlign: 'start',
                  borderRadius: 2,
                  border: `1px solid ${i === 0 ? theme.palette.primary.main : theme.palette.divider}`,
                  bgcolor: i === 0 ? theme.palette.action.selected : 'transparent',
                }}
              >
                <Typography variant="caption" fontWeight={700} color={i === 0 ? 'primary' : 'text.secondary'}>
                  {i === 0 ? 'Agora' : `${hora.hora.slice(11, 13)}h`}
                </Typography>
                <IconeCondicao icone={hora.condicao.icone} tamanho={24} cores={coresCondicao} />
                <Typography variant="body2" fontWeight={700}>{Math.round(hora.temperatura)}°</Typography>
                <Box display="flex" alignItems="center" gap={0.25} sx={{ color: hora.probabilidadeChuva >= 50 ? 'info.main' : 'text.secondary' }}>
                  <WaterDropOutlinedIcon sx={{ fontSize: 12 }} />
                  <Typography variant="caption">{Math.round(hora.probabilidadeChuva)}%</Typography>
                </Box>
              </Box>
            ))}
        </Box>

        {/* Próximos 7 dias */}
        {tituloSecao('Próximos 7 dias')}
        <Box display="flex" flexDirection="column">
          {carregando && !detalhe
            ? Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 0.75 }} />)
            : detalhe?.dias.map((dia, i) => (
              <Box
                key={dia.data}
                display="grid"
                alignItems="center"
                columnGap={1.5}
                py={1}
                sx={{
                  gridTemplateColumns: { xs: '78px 28px 1fr', sm: '120px 32px 1.2fr 1fr 1.6fr' },
                  borderTop: i === 0 ? 'none' : `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }} noWrap>
                    {nomeDoDia(dia.data, i)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{dataCurta(dia.data)}</Typography>
                </Box>
                <IconeCondicao icone={dia.condicao.icone} tamanho={26} cores={coresCondicao} />

                {/* Celular: condição + chuva + faixa de temperatura numa coluna só */}
                <Box minWidth={0} sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <Box display="flex" justifyContent="space-between" gap={1}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {dia.condicao.texto} · <Box component="span" sx={{ color: 'info.main' }}>{Math.round(dia.probabilidadeChuva)}%</Box>
                    </Typography>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {Math.round(dia.tempMax)}° <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>{Math.round(dia.tempMin)}°</Box>
                    </Typography>
                  </Box>
                </Box>

                <Box minWidth={0} sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="body2" noWrap>{dia.condicao.texto}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    UV {Math.round(dia.uvMax)} · vento {Math.round(dia.ventoMax)} km/h
                  </Typography>
                </Box>
                <Box minWidth={0} sx={{ display: { xs: 'none', sm: 'block' }, color: 'info.main' }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <WaterDropOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography variant="body2" fontWeight={600}>{Math.round(dia.probabilidadeChuva)}%</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {dia.chuvaMm.toFixed(1).replace('.', ',')} mm
                  </Typography>
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'flex' } }} alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 30, textAlign: 'right' }}>{Math.round(dia.tempMin)}°</Typography>
                  <Box flex={1} height={6} borderRadius={3} position="relative" sx={{ bgcolor: theme.palette.action.hover }}>
                    <Box
                      position="absolute"
                      top={0}
                      bottom={0}
                      borderRadius={3}
                      sx={{
                        left: `${((dia.tempMin - minSemana) / amplitude) * 100}%`,
                        right: `${((maxSemana - dia.tempMax) / amplitude) * 100}%`,
                        background: 'linear-gradient(90deg, #38BDF8, #FBBF24, #F97316)',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ width: 30 }}>{Math.round(dia.tempMax)}°</Typography>
                </Box>
              </Box>
            ))}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
          Dados: Open-Meteo. Atualizado ao abrir esta janela.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          startIcon={<OpenInNewRoundedIcon />}
          href={PrevisaoTempoService.linkPrevisaoCompleta(previsao)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Abrir no site de clima
        </Button>
        <Button variant="contained" disableElevation onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};
